import { makeAutoObservable, action, runInAction, observable } from 'mobx';
import * as ExcelJS from 'exceljs';
import { CellValidationHelper } from '../helpers/CellValidationHelper';
import { ColumnTypes } from '../models/SheetModel';
import { TemplateModel } from '../models/TemplateModel';

export class SheetStore {
    selectedSheetIndex = 0;
    MAX_SHEETS = 15;
    _sheetUpdateCounter = 0;

    constructor(rootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, {
            rootStore: false,
            selectedSheetIndex: observable,
            _sheetUpdateCounter: observable,
            resetSelection: action,
            updateSheetMetadata: action
        });
    }

    addSheet = action((baseName = null, options = {}) => {
        console.log('SheetStore: Adding sheet, current workbook:', this.currentWorkbook?.name);
        if (!this.currentWorkbook || this.visibleSheets.length >= this.MAX_SHEETS) {
            console.warn('SheetStore: Add sheet cancelled (no workbook or max sheets reached)');
            return null; // Indicate failure or limit reached
        }

        // Generate unique sheet name
        const existingSheets = this.visibleSheets;
        let sheetNumber = existingSheets.length + 1;
        let sheetName = baseName ? baseName : `Sheet ${sheetNumber}`;
        
        // Check uniqueness using the ViewModel's helper (or duplicate logic here)
        // For simplicity, let's assume basic check here. ViewModel handles final naming.
        while (this.currentWorkbook.getWorksheet(sheetName)) {
            sheetNumber++;
            sheetName = baseName ? `${baseName} (${sheetNumber - 1})` : `Sheet ${sheetNumber}`;
        }
        
        // Create new sheet
        console.log(`SheetStore: Creating new worksheet with name: ${sheetName}`);
        const newSheet = this.currentWorkbook.addWorksheet(sheetName);
        newSheet.sheetType = options.type || 'unknown'; // Store type if provided
        
        runInAction(() => {
            // Increment counter BEFORE recalculating visible sheets to force update
            this._sheetUpdateCounter++;

            // Get the index of the newly added visible sheet AFTER counter increment
            const updatedVisibleSheets = this.visibleSheets; 
            const newIndex = updatedVisibleSheets.findIndex(s => s.id === newSheet.id);
            
            console.log(`SheetStore: New sheet added. ID: ${newSheet.id}. Found Index: ${newIndex}. Visible sheets count: ${updatedVisibleSheets.length}`);

            if (newIndex === -1) {
                console.error("SheetStore: Failed to find index of newly added sheet! Falling back to length-1.");
                this.selectedSheetIndex = updatedVisibleSheets.length - 1; // Fallback
            } else {
                this.selectedSheetIndex = newIndex;
            }

            // Update selected index to the new sheet
            console.log(`SheetStore: Set selectedSheetIndex to: ${this.selectedSheetIndex}`);
            this.rootStore.fileSystemStore.markUnsaved();
        });

        // Return information about the new sheet
        return { sheet: newSheet, index: this.selectedSheetIndex };
    });

    removeSheet = action((sheetIndex) => {
        if (!this.currentWorkbook || this.visibleSheets.length <= 1) return;

        const sheets = this.visibleSheets;
        if (sheetIndex < 0 || sheetIndex >= sheets.length) return;

        const sheetToRemove = sheets[sheetIndex];
        this.currentWorkbook.removeWorksheet(sheetToRemove.id);

        runInAction(() => {
            // Adjust selected index if needed
            if (this.selectedSheetIndex >= sheetIndex) {
                 // Select the previous sheet, or the first sheet if the first was removed
                this.selectedSheetIndex = Math.max(0, this.selectedSheetIndex - 1);
            }
            this._sheetUpdateCounter++;
            this.rootStore.fileSystemStore.markUnsaved();
        });
    });

    setSheetType = action((sheetIndex, type) => {
        if (!this.currentWorkbook) return;

        const sheet = this.visibleSheets[sheetIndex];
        if (!sheet) return;

        runInAction(() => {
            sheet.sheetType = type;
            this.rootStore.fileSystemStore.markUnsaved();
        });
    });

    updateSheetName = action((sheetId, newName) => {
        console.log('[SheetStore] updateSheetName called with:', { sheetId, newName }); 
        if (!this.currentWorkbook) {
            console.warn('[SheetStore] No current workbook.');
            return;
        }
        
        const sheet = this.currentWorkbook.getWorksheet(sheetId); 
        if (!sheet) {
            console.error('[SheetStore] Sheet not found with ID:', sheetId);
            return;
        }
        if (sheet.name === newName) {
            console.log('[SheetStore] Sheet name is already the new name. No change needed.');
            return;
        }

        // The ViewModel should handle uniqueness. This is a secondary check.
        const existingSheetWithNewName = this.currentWorkbook.getWorksheet(newName);
        if (existingSheetWithNewName && existingSheetWithNewName.id !== sheet.id) {
            console.warn(`[SheetStore] Sheet name "${newName}" already exists for a different sheet (ID: ${existingSheetWithNewName.id}). ViewModel should have prevented this.`);
            // Depending on strictness, you might return. ViewModel is primary.
        }

        runInAction(() => {
            console.log(`[SheetStore] Updating sheet ID ${sheet.id} name from "${sheet.name}" to "${newName}"`);
            sheet.name = newName;
            this._sheetUpdateCounter++; // ADDED: Ensure MobX re-evaluates dependents like visibleSheets
            this.rootStore.fileSystemStore.markUnsaved();
            console.log(`[SheetStore] Sheet name updated. _sheetUpdateCounter: ${this._sheetUpdateCounter}`);
        });
    });

    selectSheet = action((index) => {
        const visibleSheetCount = this.visibleSheets.length;
        if (index >= 0 && index < visibleSheetCount) {
             // Check if index is actually changing to avoid unnecessary updates
            if (this.selectedSheetIndex !== index) {
                this.selectedSheetIndex = index;
                // Optionally trigger data loading for the new sheet if needed
                // this.rootStore.sheetDataStore.loadSheet(this.currentSheet.name);
            }
        } else {
            console.warn(`Attempted to select invalid sheet index: ${index}. Visible sheets: ${visibleSheetCount}`);
        }
    });

    updateSheetMetadata = action((sheetIndex, metadata) => {
        if (!this.currentWorkbook) return;
        const sheet = this.visibleSheets[sheetIndex];
        if (!sheet) return;

        runInAction(() => {
            // Check specifically for 'color' in metadata
            if ('color' in metadata) {
                const colorValue = metadata.color;
                console.log(`SheetStore: Setting tab color for sheet ${sheetIndex} (${sheet.name}) to ${colorValue}`);
                if (colorValue) {
                    // Convert #RRGGBB to FFRRGGBB for ExcelJS
                    const argbColor = 'FF' + colorValue.replace('#', '');
                    sheet.properties.tabColor = { argb: argbColor };
                    // Store the #RRGGBB on our custom property for easy UI access
                    sheet.color = colorValue; 
                } else {
                    // Remove color if null/undefined/empty string is passed
                    sheet.properties.tabColor = undefined;
                    delete sheet.color;
                }
            }
            // Add handling for other metadata properties if needed
            // e.g., if ('sheetType' in metadata) { sheet.sheetType = metadata.sheetType; }

            // No need to call _updateMetadataSheet anymore
            this.rootStore.fileSystemStore.markUnsaved();
        });
    });

    resetSelection = action(() => {
        console.log(`SheetStore: Resetting selectedSheetIndex from ${this.selectedSheetIndex} to 0`);
        this.selectedSheetIndex = 0;
        this._sheetUpdateCounter++;
        console.log(`SheetStore: Incremented _sheetUpdateCounter to ${this._sheetUpdateCounter}`);
    });

    moveSheet = action((fromIndex, toIndex) => {
        if (!this.currentWorkbook) return;

        const visibleSheets = this.visibleSheets; // Use the getter
        const numVisibleSheets = visibleSheets.length;

        // Corrected the check to use numVisibleSheets
        if (fromIndex < 0 || fromIndex >= numVisibleSheets ||
            toIndex < 0 || toIndex >= numVisibleSheets ||
            fromIndex === toIndex) {
            return; // Invalid indices or no move needed
        }

        runInAction(() => {
            // Get the actual worksheet object being moved
            const sheetToMove = visibleSheets[fromIndex];
            if (!sheetToMove) return; // Should not happen with valid index

            // --- Reorder using orderNo --- 
            // Create a mutable copy of the visible sheets array to reorder
            const reorderedVisibleSheets = [...visibleSheets];
            const [movedSheet] = reorderedVisibleSheets.splice(fromIndex, 1);
            reorderedVisibleSheets.splice(toIndex, 0, movedSheet);

            // Assign new orderNo based on the reordered visible sheets
            // Keep track of the order index for visible sheets
            let visibleOrderIndex = 0; 
            // Keep track of order index for hidden sheets (relative to end)
            let hiddenOrderIndexOffset = 0;
            this.currentWorkbook.worksheets.forEach(ws => {
                // Find the new position of this worksheet in the reordered visible list
                const newVisibleIndex = reorderedVisibleSheets.findIndex(visibleSheet => visibleSheet.id === ws.id);

                if (newVisibleIndex !== -1) {
                    // It's a visible sheet, assign its new order based on the visible sequence
                    // Assuming 0-based orderNo
                    ws.orderNo = newVisibleIndex; 
                    visibleOrderIndex++; // Increment only for visible sheets
                } else if (ws.name !== '_React2Excel_META_x7z9') {
                    // It's a hidden sheet (not metadata)
                    // Assign it an orderNo *after* all visible sheets, preserving relative hidden order
                    ws.orderNo = numVisibleSheets + hiddenOrderIndexOffset; 
                    hiddenOrderIndexOffset++;
                }
                // Metadata sheet order doesn't matter or be changed
            });

            // --- Update selected index --- 
            if (this.selectedSheetIndex === fromIndex) {
                this.selectedSheetIndex = toIndex;
            } else if (fromIndex < this.selectedSheetIndex && toIndex >= this.selectedSheetIndex) {
                this.selectedSheetIndex--;
            } else if (fromIndex > this.selectedSheetIndex && toIndex <= this.selectedSheetIndex) {
                this.selectedSheetIndex++;
            }

            this._sheetUpdateCounter++; // Force update for visibleSheets getter
            console.log(`SheetStore: Incremented counter to ${this._sheetUpdateCounter} after move.`);

            // Log the computed visible sheets *after* updates
            const currentVisibleOrder = this.visibleSheets.map(s => ({ name: s.name, orderNo: s.orderNo }));
            console.log("SheetStore: visibleSheets order AFTER moveSheet action:", currentVisibleOrder);

            this.rootStore.fileSystemStore.markUnsaved();
        });
    });

    // Helper methods
    get currentWorkbook() {
        // Ensure index is valid before accessing
        if (!this.rootStore?.workbookStore || // Add safety checks
            this.rootStore.workbookStore.selectedWorkbookIndex < 0 ||
            this.rootStore.workbookStore.selectedWorkbookIndex >= this.rootStore.workbookStore.workbooks.length) {
            return null;
        }
        return this.rootStore.workbookStore.workbooks[this.rootStore.workbookStore.selectedWorkbookIndex];
    }

    get visibleSheets() {
        // Incrementing counter ensures MobX re-evaluates this getter when needed
        const _counter = this._sheetUpdateCounter; 
        const workbook = this.currentWorkbook; // Use the getter
        if (!workbook) return [];
        
        // Filter first, then sort by orderNo
        // Make sure worksheet objects are accessed correctly
        const sheets = workbook.worksheets || []; // Handle case where worksheets might be undefined briefly
        return sheets
            .filter(sheet => sheet && sheet.name !== '_React2Excel_META_x7z9' &&
                             sheet.state !== 'hidden' &&
                             sheet.state !== 'veryHidden')
            .sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0)); // Sort by orderNo
    }

    get currentSheet() {
        const visible = this.visibleSheets; // Use the getter
        if (!visible || !visible.length || this.selectedSheetIndex < 0 || this.selectedSheetIndex >= visible.length) {
            return null; // No sheet selected or available
        }
        return visible[this.selectedSheetIndex];
    }

    // Placeholder for adding columns to a sheet
    addColumnsToSheet = action((sheetId, columnsToAdd) => {
        console.log(`SheetStore: Adding columns to sheet ID ${sheetId}:`, columnsToAdd);
        // Find sheet by ID
        const sheet = this.currentWorkbook.getWorksheet(sheetId);
        if (!sheet) {
            console.error("Sheet not found with ID:", sheetId);
            return;
        }
        
        // Ensure sheet.columns is initialized if it's the first time adding columns
        if (!sheet.columns || sheet.columns.length === 0) {
            sheet.columns = [];
            // Ensure header row exists if sheet is completely empty
            if (sheet.rowCount === 0) {
                sheet.addRow([]); // Add an empty row for headers
            }
        }

        const existingHeadings = sheet.columns.map(col => col.header || '');
        const headerRow = sheet.getRow(1); // Assuming headers are always row 1
        let nextAvailableColIndex = sheet.columnCount + 1;

        columnsToAdd.forEach(colInfo => {
            // Check if a column with the required *type* already exists
            const typeExists = sheet.columns.some((existingCol, index) => {
                // Need a way to map ExcelJS column definition back to our ColumnType ID
                // This is complex. For now, let's add based on name uniqueness.
                return existingHeadings[index]?.toLowerCase() === colInfo.name.toLowerCase();
            });

            if (!typeExists) {
                const newColIndex = nextAvailableColIndex++;
                const newHeading = colInfo.name;
                console.log(`SheetStore: Adding column '${newHeading}' (Type: ${colInfo.type.id}) at index ${newColIndex}`);

                // Add header to the sheet
                headerRow.getCell(newColIndex).value = newHeading;

                // Define the column properties for ExcelJS
                // Note: sheet.columns refers to ExcelJS column definitions, not our Column model
                const newExcelCol = sheet.getColumn(newColIndex);
                newExcelCol.header = newHeading;
                newExcelCol.key = newHeading; // Use heading as key for simplicity
                newExcelCol.width = 15; // Default width

                // Apply validation based on the ColumnType
                CellValidationHelper.setupNewColumnValidation(sheet, newColIndex, colInfo.type);
                if (colInfo.type.hasCustomValidation && colInfo.type.defaultOptions) {
                    CellValidationHelper.updateColumnOptions(sheet, newColIndex, colInfo.type.defaultOptions, colInfo.type);
                }

                // Add to our conceptual list of headings for subsequent checks in this loop
                existingHeadings.push(newHeading);
            } else {
                console.log(`SheetStore: Column for type '${colInfo.type.label}' (proposed name: ${colInfo.name}) seems to already exist. Skipping.`);
            }
        });

        // After adding all columns, update the sheet.columns array to reflect reality
        // This step might be redundant if getColumn/headerRow modifications are enough,
        // but explicitly setting it can help consistency.
        sheet.columns = sheet.columns; // Re-assign to potentially trigger updates if needed

        this.rootStore.fileSystemStore.markUnsaved();
    });

    // Helper function (can be placed inside SheetStore or as a utility)
    // Basic deep copy for simple cell values/styles - might need enhancement for complex types
    _copyCellValueAndStyle = (sourceCell, targetCell) => {
        // Simple value copy (handle different types)
        if (sourceCell.value !== null && typeof sourceCell.value === 'object' && 'richText' in sourceCell.value) {
            targetCell.value = { richText: JSON.parse(JSON.stringify(sourceCell.value.richText)) }; // Deep copy rich text array
        } else if (sourceCell.value instanceof Date) {
            targetCell.value = new Date(sourceCell.value);
        } else {
            // Basic types or null/undefined
            targetCell.value = sourceCell.value;
        }

        // Copy style properties (more robust deep copy attempt)
        if (sourceCell.style && Object.keys(sourceCell.style).length > 0) {
            targetCell.style = JSON.parse(JSON.stringify(sourceCell.style));
        }
        if (sourceCell.numFmt) {
            targetCell.numFmt = sourceCell.numFmt;
        }

        // Copy data validation if it exists
        if (sourceCell.dataValidation) {
            targetCell.dataValidation = JSON.parse(JSON.stringify(sourceCell.dataValidation));
        }
    };

    // Placeholder for importing a sheet from a template
    importSheetFromTemplate = action((templateId) => {
        console.log(`[SheetStore] Attempting to import sheet from template ID: ${templateId}`);

        if (!this.currentWorkbook || this.visibleSheets.length >= this.MAX_SHEETS) {
            console.warn("[SheetStore] Cannot import sheet: No current workbook or max sheets reached.");
            return null;
        }

        try {
            // 1. Get template creation function
            const templateInfo = TemplateModel.getTemplates().find(t => t.id === templateId);
            if (!templateInfo || !templateInfo.createFn) {
                console.error(`[SheetStore] Template with ID ${templateId} not found or invalid createFn.`);
                throw new Error(`Template with ID ${templateId} not found or invalid.`);
            }
            console.log(`[SheetStore] Found template info for ID ${templateId}:`, templateInfo);

            // 2. Create the template workbook in memory
            console.log(`[SheetStore] Executing createFn for template ID: ${templateId}`);
            const templateWorkbook = templateInfo.createFn();
            if (!templateWorkbook || !templateWorkbook.worksheets || templateWorkbook.worksheets.length === 0) {
                console.error(`[SheetStore] createFn for template ID ${templateId} did not return a valid workbook or workbook has no worksheets.`);
                throw new Error(`Template createFn for ${templateId} failed to produce a valid workbook.`);
            }
            console.log(`[SheetStore] Template workbook created. Worksheet count: ${templateWorkbook.worksheets.length}`);

            // 3. Extract the first relevant sheet
            const templateSheet = templateWorkbook.worksheets.find(ws => ws.state !== 'hidden' && ws.state !== 'veryHidden');
            if (!templateSheet) {
                console.error(`[SheetStore] No visible sheet found in template workbook for ID ${templateId}.`);
                throw new Error(`No visible sheet found in template ${templateId}.`);
            }
            console.log(`[SheetStore] Found templateSheet: Name: "${templateSheet.name}", Rows: ${templateSheet.rowCount}, Cols: ${templateSheet.columnCount}`);
            if (templateSheet.rowCount === 0) {
                console.warn(`[SheetStore] templateSheet "${templateSheet.name}" has 0 rows. The imported sheet will likely be empty.`);
            }

            // 4. Generate a unique name in the target workbook
            let targetSheetName = templateSheet.name; 
            let counter = 1;
            while (this.currentWorkbook.getWorksheet(targetSheetName)) {
                targetSheetName = `${templateSheet.name} (${counter})`;
                counter++;
            }

            console.log(`[SheetStore] Creating new sheet in target workbook named: ${targetSheetName}`);
            // 5. Add a new blank sheet to the current workbook
            const newSheet = this.currentWorkbook.addWorksheet(targetSheetName);

            // --- Copy Process --- 
            console.log("[SheetStore] Starting copy process...");
            // 5a. Copy Column Definitions (Headers, Widths)
            if (templateSheet.columns && templateSheet.columns.length > 0) {
                console.log(`[SheetStore] Copying ${templateSheet.columns.length} column definitions. First column header: "${templateSheet.columns[0]?.header}"`);
                newSheet.columns = templateSheet.columns.map(col => ({ ...col })); 
            } else {
                console.log("[SheetStore] No column definitions found on templateSheet to copy.");
            }

            console.log("[SheetStore] Copying rows and cell data/styles/validation...");
            let rowsCopied = 0;
            templateSheet.eachRow({ includeEmpty: true }, (templateRow, rowNumber) => {
                rowsCopied++;
                const newRow = newSheet.getRow(rowNumber);
                let cellsCopiedThisRow = 0;
                templateRow.eachCell({ includeEmpty: true }, (templateCell, colNumber) => {
                    cellsCopiedThisRow++;
                    const newCell = newRow.getCell(colNumber);
                    this._copyCellValueAndStyle(templateCell, newCell);
                     if (rowNumber === 1 && cellsCopiedThisRow <= 3) { // Log first few cells of first row
                        console.log(`[SheetStore] Copied R${rowNumber}C${colNumber}: Template Val: "${templateCell.value}", New Val: "${newCell.value}"`);
                    }
                });
                newRow.height = templateRow.height;
                newRow.outlineLevel = templateRow.outlineLevel;
                newRow.commit(); 
            });
            console.log(`[SheetStore] Finished copying rows. Total rows processed: ${rowsCopied}. New sheet row count: ${newSheet.rowCount}`);

            console.log("[SheetStore] Copying conditional formatting...");
            // 5c. Copy Conditional Formatting Rules
            // WARNING: This relies on internal structure and might break with ExcelJS updates
            if (templateSheet.conditionalFormattings) {
                newSheet.conditionalFormattings = JSON.parse(JSON.stringify(templateSheet.conditionalFormattings));
            }

            // 5d. Copy Merged Cells (if any)
            // TODO: Implement if needed. Requires iterating templateSheet.model.merges

            // 5e. Copy Sheet Properties (e.g., sheetType from template if desired)
            newSheet.sheetType = templateSheet.sheetType || 'unknown';
            // Copy tab color if present in template (unlikely, but possible)
            if (templateSheet.properties?.tabColor) {
                newSheet.properties.tabColor = { ...templateSheet.properties.tabColor };
                // Set our UI helper property too
                if (templateSheet.properties.tabColor.argb?.length === 8) {
                    newSheet.color = '#' + templateSheet.properties.tabColor.argb.substring(2);
                }
            }
            // Copy sheet view settings (like frozen panes, zoom level) if needed
            newSheet.views = templateSheet.views ? JSON.parse(JSON.stringify(templateSheet.views)) : [];
            
            console.log(`[SheetStore] Copy process finished for sheet: "${targetSheetName}". Final newSheet row count: ${newSheet.rowCount}, column count: ${newSheet.columnCount}`);

            // 6. Update store state
            runInAction(() => {
                this._sheetUpdateCounter++;
                const updatedVisibleSheets = this.visibleSheets;
                const newIndex = updatedVisibleSheets.findIndex(s => s.id === newSheet.id);

                if (newIndex === -1) {
                    console.error("SheetStore: Failed to find index of imported sheet!");
                    this.selectedSheetIndex = updatedVisibleSheets.length - 1;
                } else {
                    this.selectedSheetIndex = newIndex;
                }
                console.log(`SheetStore: Imported sheet selected index set to: ${this.selectedSheetIndex}`);
                this.rootStore.fileSystemStore.markUnsaved();
            });

            return { sheet: newSheet, index: this.selectedSheetIndex };

        } catch (error) {
            console.error(`Error importing sheet from template ${templateId}:`, error);
            // Optionally show user feedback
            alert(`Failed to add sheet from template: ${error.message}`);
            return null;
        }
    });
}